import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";

const faqs = [
  {
    id: 1,
    category: "Services",
    question: "How often should I have my septic tank pumped?",
    answer: "Most septic tanks should be pumped every 3-5 years, depending on tank size, household size, and usage patterns. We recommend annual inspections to determine your specific pumping schedule.",
  },
  {
    id: 2,
    category: "Services",
    question: "What is included in a septic system inspection?",
    answer: "Our comprehensive inspections include visual assessment of the tank, drain field evaluation, water level checks, and identification of any potential issues. We provide a detailed report with recommendations.",
  },
  {
    id: 3,
    category: "Services",
    question: "Do you handle emergency septic repairs?",
    answer: "Yes! We offer 24/7 emergency septic repair services. Contact us immediately if you experience backups, odors, or system failures. We prioritize emergency calls and can often respond the same day.",
  },
  {
    id: 4,
    category: "Services",
    question: "What are the signs my septic system needs repair?",
    answer: "Common warning signs include slow drains, sewage backups, unusual odors, wet spots in the yard, and lush grass patches over the drain field. If you notice any of these, contact us for an inspection.",
  },
  {
    id: 5,
    category: "Pricing",
    question: "How much does septic pumping cost?",
    answer: "Pumping costs typically range from $300-$500 depending on tank size and accessibility. We provide free estimates before any work begins. Our EZ Maintenance Plans offer discounted rates for regular maintenance.",
  },
  {
    id: 6,
    category: "Pricing",
    question: "What are your EZ Maintenance Plans?",
    answer: "Our EZ Maintenance Plans provide regular maintenance, priority scheduling, and discounted rates. Plans include annual inspections, recommended pumping, and emergency support. Contact us for pricing tailored to your needs.",
  },
  {
    id: 7,
    category: "Pricing",
    question: "Do you offer payment plans?",
    answer: "Yes, we offer flexible payment options for larger projects. Contact our office to discuss financing options that work for your budget.",
  },
  {
    id: 8,
    category: "Maintenance",
    question: "What should I avoid putting in my septic system?",
    answer: "Never flush non-biodegradable items, grease, chemicals, medications, or excessive amounts of paper. These can damage your system. Use septic-safe products and dispose of hazardous materials properly.",
  },
  {
    id: 9,
    category: "Maintenance",
    question: "How can I maintain my septic system?",
    answer: "Regular maintenance includes: pumping every 3-5 years, avoiding harmful substances, fixing leaks promptly, protecting the drain field from heavy vehicles, and scheduling annual inspections.",
  },
  {
    id: 10,
    category: "Maintenance",
    question: "What is a drain field and how long do they last?",
    answer: "The drain field is where treated water from your septic tank filters into the soil. With proper maintenance, drain fields typically last 25-30 years. Regular pumping and inspections help extend their lifespan.",
  },
];

interface FAQItemProps {
  faq: typeof faqs[0];
  isOpen: boolean;
  onToggle: () => void;
}

function ContactUsButton() {
  const [, navigate] = useLocation();
  return (
    <Button
      size="lg"
      className="bg-accent hover:bg-accent/90 text-accent-foreground"
      onClick={() => navigate("/schedule")}
    >
      Contact Us Now
    </Button>
  );
}

function FAQItem({ faq, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-start justify-between bg-background hover:bg-background/80 transition-colors"
      >
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-accent-text mb-1">{faq.category}</p>
          <h3 className="text-base font-semibold text-foreground">{faq.question}</h3>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-accent flex-shrink-0 ml-4 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="px-6 py-4 bg-background/50 border-t border-border">
          <p className="text-foreground/80 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQSection() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = ["Services", "Pricing", "Maintenance"];
  const filteredFaqs = selectedCategory
    ? faqs.filter((faq) => faq.category === selectedCategory)
    : faqs;

  return (
    <section className="py-20 bg-background">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            Find answers to common questions about our septic services, pricing, and maintenance.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            onClick={() => setSelectedCategory(null)}
            className={selectedCategory === null ? "bg-accent hover:bg-accent/90 text-accent-foreground" : ""}
          >
            All
          </Button>
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? "bg-accent hover:bg-accent/90 text-accent-foreground" : ""}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="max-w-3xl mx-auto space-y-4">
          {filteredFaqs.map((faq) => (
            <FAQItem
              key={faq.id}
              faq={faq}
              isOpen={openId === faq.id}
              onToggle={() => setOpenId(openId === faq.id ? null : faq.id)}
            />
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8 md:p-12 text-center">
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Didn't find your answer?
          </h3>
          <p className="text-foreground/70 mb-6 max-w-2xl mx-auto">
            Our team is ready to help! Contact us directly for personalized assistance with your septic system needs.
          </p>
          <ContactUsButton />
        </div>
      </div>
    </section>
  );
}
